import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import GlobalSearch from './GlobalSearch';
import MobileBottomNav from './MobileBottomNav';
import QuickAdd from './QuickAdd';
import { AiAssistBubble } from './AiAssist/AiAssistBubble';
import { isPageAllowed } from '../lib/pageRights';
import {
  LayoutDashboard,
  BookOpen,
  ShieldCheck,
  FileText,
  Settings,
  LogOut,
  Search,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Upload,
  Wand2,
  MapPin,
  ScrollText,
} from 'lucide-react';

// Primary navigation — always visible (subject to IDP page rights).
const PRIMARY_NAV = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, pageKey: 'dashboard' },
  { path: '/kennisbank', label: 'Kennisbank', icon: BookOpen, pageKey: 'kennisbank' },
  { path: '/compliance', label: 'Deadlines', icon: ShieldCheck, pageKey: 'compliance' },
  { path: '/contracten', label: 'Contracten', icon: ScrollText, pageKey: 'contracten' },
  { path: '/instellingen', label: 'Instellingen', icon: Settings, pageKey: 'instellingen' },
];

// "Beheer" group — owner-only AND subject to IDP page rights.
const BEHEER_NAV = [
  { path: '/ai-normalisatie', label: 'AI Normalisatie', icon: Wand2, pageKey: 'ai_normalisatie' },
  { path: '/logboek', label: 'Logboek', icon: FileText, pageKey: 'logboek' },
];

export default function AppShell({ children }) {
  const { user, paginas, logout, authAxios } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Sidebar locations
  const [locationsOpen, setLocationsOpen] = useState(() => localStorage.getItem('tw1_sidebar_locs') !== 'false');
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [sidebarLocations, setSidebarLocations] = useState([]);
  const [entryCounts, setEntryCounts] = useState({});
  const [scoreMap, setScoreMap] = useState({});

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load sidebar location data
  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [meRes, locsRes, countsRes, scoresRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/locations'),
          api.get('/locations/entry-counts'),
          api.get('/compliance-scores').catch(() => ({ data: { scores: [] } })),
        ]);
        const pinned = meRes.data.pinned_locations || [];
        const allLocs = locsRes.data || [];
        const pinnedLocs = pinned.map(pid => allLocs.find(l => l.location_id === pid)).filter(Boolean);
        setSidebarLocations(pinnedLocs.length > 0 ? pinnedLocs : allLocs.filter(l => l.status === 'active').slice(0, 8));
        setEntryCounts(countsRes.data.counts || {});
        const sm = {};
        (scoresRes.data.scores || []).forEach(s => { sm[s.location_id] = s.score; });
        setScoreMap(sm);
      } catch (e) { /* silent */ }
    };
    if (user) load();
  }, [user, authAxios]);

  const toggleLocationsOpen = () => {
    const next = !locationsOpen;
    setLocationsOpen(next);
    localStorage.setItem('tw1_sidebar_locs', next.toString());
  };


  const getCurrentTitle = useCallback(() => {
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path.startsWith('/kennisbank/nieuw')) return 'Nieuw Item';
    if (path.startsWith('/kennisbank/upload')) return 'Document Uploaden';
    if (path.includes('/bewerken')) return 'Item Bewerken';
    if (path.startsWith('/kennisbank/document/')) return 'Document Review';
    if (path.startsWith('/kennisbank/')) return 'Item Details';
    if (path === '/kennisbank') return 'Kennisbank';
    if (path === '/compliance') return 'Deadlines';
    if (path === '/contracten') return 'Contracten';
    if (path.startsWith('/locatie/')) return 'Locatie';
    if (path === '/inzichten') return 'AI Inzichten';
    if (path === '/ai-normalisatie') return 'AI Normalisatie';
    if (path === '/logboek') return 'Logboek';
    if (path === '/instellingen') return 'Instellingen';
    return 'TW1 Vault';
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/kennisbank') return location.pathname.startsWith('/kennisbank') || location.pathname.startsWith('/locatie');
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <button
        key={item.path}
        data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
        onClick={() => {
          navigate(item.path);
          setMobileMenuOpen(false);
        }}
        className={`group flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm transition-colors duration-200
          ${active
            ? 'bg-[#2E75B6]/20 text-white shadow-sm'
            : 'text-white/50 hover:text-white hover:bg-white/[0.05]'
          }`}
      >
        <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'opacity-100 text-[#58A6FF]' : 'opacity-70 group-hover:opacity-100'}`} />
        <span className="nav-label flex-1 text-left">{item.label}</span>
        {active && <ChevronRight className="w-3.5 h-3.5 ml-auto nav-label" />}
      </button>
    );
  };

  // Beheer group: owner-only, further filtered by IDP page rights.
  const beheerItems = user?.role === 'owner'
    ? BEHEER_NAV.filter((item) => isPageAllowed(paginas, item.pageKey))
    : [];

  return (
    <div className="app-shell">
      {/* Sidebar - always dark */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open translate-x-0' : ''}`} data-testid="sidebar">
        {/* Brand */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-white/10 overflow-hidden">
              <img src="/images/logo-icon-80.png" alt="TW1" className="w-8 h-8 object-contain" />
            </div>
            <div className="brand-text">
              <h1 className="font-heading text-base font-bold text-white tracking-tight">TW1 Vault</h1>
              <p className="text-[10px] text-white/50 uppercase tracking-wider">Kennisbank</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {PRIMARY_NAV.filter((item) => isPageAllowed(paginas, item.pageKey)).map(renderNavItem)}

          {/* Beheer group (owner-only) */}
          {beheerItems.length > 0 && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="sidebar-beheer-group">
              <p className="px-3 mb-1.5 text-[10px] uppercase tracking-wider text-white/30 font-semibold">Beheer</p>
              <div className="space-y-1">
                {beheerItems.map(renderNavItem)}
              </div>
            </div>
          )}
        </nav>

        {/* Sidebar Locations */}
        {sidebarLocations.length > 0 && (
          <div className="px-3 pb-2 border-t border-white/10 pt-3" data-testid="sidebar-locations">
            <button
              onClick={toggleLocationsOpen}
              data-testid="sidebar-locations-toggle"
              className="flex items-center justify-between w-full px-2 mb-1.5"
            >
              <span className="text-[10px] uppercase tracking-wider text-white/30 font-semibold">Locaties</span>
              <ChevronDown className={`w-3 h-3 text-white/30 transition-transform ${locationsOpen ? 'rotate-180' : ''}`} />
            </button>
            {locationsOpen && (
              <div className="space-y-0.5">
                {(locationsExpanded ? sidebarLocations : sidebarLocations.slice(0, 8)).map(loc => {
                  const score = scoreMap[loc.location_id];
                  const count = entryCounts[loc.location_id] || 0;
                  const active = location.pathname === `/locatie/${loc.location_id}`;
                  const dotColor = score == null ? 'bg-white/20' : score >= 80 ? 'bg-green-400' : score >= 60 ? 'bg-amber-400' : 'bg-red-400';

                  return (
                    <button
                      key={loc.location_id}
                      data-testid={`sidebar-loc-${loc.location_id}`}
                      onClick={() => { navigate(`/locatie/${loc.location_id}`); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-2.5 w-full rounded-lg px-3 py-1.5 text-xs transition-colors ${
                        active ? 'bg-[#2E75B6]/20 text-white' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} title={score != null ? `Score: ${score}` : ''} />
                      <span className="flex-1 text-left truncate nav-label">{loc.name}</span>
                      {count > 0 && (
                        <span className="text-[9px] text-white/25 tabular-nums nav-label">{count}</span>
                      )}
                    </button>
                  );
                })}
                {sidebarLocations.length > 8 && !locationsExpanded && (
                  <button
                    onClick={() => setLocationsExpanded(true)}
                    className="w-full text-left px-3 py-1 text-[10px] text-white/30 hover:text-white/50 transition-colors"
                  >
                    Meer... ({sidebarLocations.length - 8})
                  </button>
                )}
                <button
                  onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2.5 w-full rounded-lg px-3 py-1.5 text-[10px] text-white/25 hover:text-white/50 transition-colors"
                >
                  <MapPin className="w-3 h-3" />
                  <span className="nav-label">Alle locaties</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bottom section: theme toggle + user */}
        <div className="px-3 py-4 border-t border-white/10 space-y-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            className="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors duration-200"
            title={theme === 'dark' ? 'Licht thema' : 'Donker thema'}
          >
            {theme === 'dark' ? (
              <Sun className="w-[18px] h-[18px] flex-shrink-0" />
            ) : (
              <Moon className="w-[18px] h-[18px] flex-shrink-0" />
            )}
            <span className="nav-label">{theme === 'dark' ? 'Licht thema' : 'Donker thema'}</span>
          </button>

          {/* User section */}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-[#2E75B6]/30 flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="nav-label flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-white/50 capitalize">{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors duration-200"
              title="Uitloggen"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content pb-[64px] md:pb-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-[#0D1117]/80 backdrop-blur-md border-b border-slate-200/80 dark:border-[#21262D] transition-colors duration-200">
          <div className="flex items-center justify-between px-3 sm:px-6 py-2 sm:py-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                className="md:hidden p-2 rounded-md hover:bg-slate-100 dark:hover:bg-[#21262D] text-foreground"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <h2 className="font-heading text-base sm:text-lg font-semibold text-foreground tracking-tight truncate" data-testid="page-title">
                {getCurrentTitle()}
              </h2>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="cmdk-open-button"
                className="flex items-center gap-2 p-2 sm:px-3 sm:py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-sm text-slate-500 dark:text-[#8B949E] hover:text-slate-700 dark:hover:text-[#C9D1D9] hover:border-slate-300 dark:hover:border-[#484F58] hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors duration-200"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Zoeken...</span>
                <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 dark:text-[#484F58] bg-slate-100 dark:bg-[#21262D] rounded border border-slate-200 dark:border-[#30363D]">
                  <span className="text-xs">&#8984;</span>K
                </kbd>
              </button>
              <div className="hidden sm:flex items-center gap-2 text-sm text-foreground">
                <span>{user?.name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-[#21262D] text-slate-500 dark:text-[#8B949E] capitalize">{user?.role}</span>
              </div>
              <button
                onClick={() => navigate('/kennisbank/upload')}
                className="p-2 rounded-lg text-slate-400 dark:text-[#484F58] hover:text-[#2E75B6] hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors duration-200"
                title="Document uploaden"
                data-testid="appshell-upload-button"
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-3 sm:p-6 page-fade-in">
          {children}
        </div>
      </main>

      {/* Global Search */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Quick Add FAB */}
      <QuickAdd />

      {/* AI Assist Bubble */}
      <AiAssistBubble />

      <MobileBottomNav onSearchOpen={setSearchOpen} />

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}
